import React, { useMemo } from 'react'
import isNil from 'lodash/isNil'
import { ButtonGroup, Image, Stack } from 'react-bootstrap'
import type { Asset } from '@ztechapps/common.contract'
import { AssetTypeEnum } from '@ztechapps/common.contract'
import { useError, useToast } from '@ztechapps/common.hook'
import type { DragSortHandleProps } from '@ztechapps/webui.util'
import { DragHandle, ShowIf } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import { useAssetPageContext } from '../Context'
import { DeleteAssetButton } from '../DeleteAssetButton'
import { AssetDetails, CopyUrlDropdown, DragHandleButton, OpenFileButton } from '../shared'

type ItemProps = {
  item: Asset
  handleProps?: DragSortHandleProps
}

export const Item = ({ item, handleProps }: ItemProps) => {
  const context = useAssetPageContext()
  const canEdit = context.canEdit
  const pdfIcon = context.pdfIcon
  const fileIcon = context.fileIcon
  const onReorder = context.onReorder
  const coverImage = context.coverImage
  const originalUrl = item?.url
  const imageUrl = item.mediumUrl || originalUrl
  const thumbUrl = item?.thumbUrl || imageUrl

  const { onError } = useError()
  const { showSuccess } = useToast()

  const isCoverImage = useMemo<boolean>(() => {
    return coverImage === imageUrl
  }, [coverImage, imageUrl])

  return (
    <div className="h-100">
      <div className="shadow bg-body">
        <div className={`card ${isCoverImage ? 'border border-2 border-warning' : ''} `}>
          <div style={{ position: 'relative' }}>
            <ShowIf condition={item?.type === AssetTypeEnum.image}>
              <Image src={thumbUrl} style={{ height: 220, width: '100%' }} alt={thumbUrl} />
            </ShowIf>
            <ShowIf condition={item?.type === AssetTypeEnum.video}>
              <div className="embed-responsive embed-responsive-16by9">
                <iframe
                  title="Video viewer"
                  className="embed-responsive-item"
                  src={item?.url}
                  width="220"
                  height="220"
                />
              </div>
            </ShowIf>
            <ShowIf condition={item?.type === AssetTypeEnum.document}>
              <Image src={pdfIcon} style={{ height: 220, width: '100%' }} alt={pdfIcon} />
            </ShowIf>
            <ShowIf condition={item?.type === AssetTypeEnum.raw}>
              <Image src={fileIcon} style={{ height: 220, width: '100%' }} alt={fileIcon} />
            </ShowIf>
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <ButtonGroup>
                <ShowIf condition={!isNil(coverImage) && !isCoverImage && item?.type === 'image'}>
                  <BusyButton
                    title="Set as cover image"
                    onClick={async () => {
                      try {
                        await onReorder({ coverImage: item?.url })
                        showSuccess({ message: 'Done updating the cover image' })
                      } catch (e) {
                        onError(e)
                      }
                    }}
                    icon="image"
                    bsSize="sm"
                  />
                </ShowIf>
                <DragHandle
                  disabled={!canEdit}
                  title="Drag to sort the assets"
                  As={DragHandleButton}
                  {...handleProps}
                />
                <CopyUrlDropdown asset={item} />
                <OpenFileButton url={item?.url} />
                <DeleteAssetButton _id={item?._id} />
              </ButtonGroup>
            </div>
          </div>
          <Stack className="p-2">
            <AssetDetails item={item} />
            {context.renderAssetStatus?.(item, 'Status')}
          </Stack>
        </div>
      </div>
    </div>
  )
}
